import { useEffect, useState, useRef } from 'react';

const twBreakpoint: Record<'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs', string> = {
  xxl: '1536',
  xl: '1280',
  lg: '1024',
  md: '768',
  sm: '640',
  xs: '576',
};

const breakpointInt = (str = '') => {
  return parseInt(str.replace('px', ''));
};

const breakpoint = {
  xxl: breakpointInt(twBreakpoint.xxl),
  xl: breakpointInt(twBreakpoint.xl),
  lg: breakpointInt(twBreakpoint.lg),
  md: breakpointInt(twBreakpoint.md),
  sm: breakpointInt(twBreakpoint.sm),
  xs: breakpointInt(twBreakpoint.xs),
};

const isClient = typeof window !== 'undefined';

const getResponsiveState = () => {
  if (!isClient) {
    return {
      windowWidth: 0,
      larger: {},
      smaller: {},
    };
  }
  
  const currentWindowWidth = window.innerWidth;
  
  const getAllSizes = (comparator = 'smaller') => {
    return Object.fromEntries(
      Object.entries(breakpoint).map(([key, value]) => [
        key,
        comparator === 'larger'
          ? currentWindowWidth >= value
          : currentWindowWidth < value,
      ]),
    );
  };
  
  return {
    windowWidth: currentWindowWidth,
    larger: getAllSizes('larger'),
    smaller: getAllSizes('smaller'),
  };
};

const useResponsive = (debounceDelay: number = 50) => {
  const [responsive, setResponsive] = useState(() => {
    return getResponsiveState(); 
  });

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isClient) return;

    const handleResize = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        setResponsive(getResponsiveState());
      }, debounceDelay);
    };

    setResponsive(getResponsiveState());
    
    window.addEventListener('resize', handleResize);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [debounceDelay]);

  return responsive;
};

export default useResponsive;